import { MarkerType, Position, type Edge, type Node } from '@xyflow/react';
import type { PlatformArchitectureModel, PlatformArchitectureProjection, PlatformProcessStep, PlatformRecord, PlatformRelationship } from '../../hooks/usePlatformArchitecture';
import type { ArchitectureReview, CapabilityReview, ChangeProposal, InfrastructureNodeData, InfrastructureTopology, ProcessStep } from './PlatformArchitectureDesign';

const iconByType: Record<string, string> = {
  'microsoft.network/azurefirewalls': 'azure-firewall',
  'microsoft.network/applicationgateways': 'application-gateway',
  'microsoft.network/privateendpoints': 'private-endpoint',
  'microsoft.network/virtualnetworks': 'virtual-network',
  'microsoft.web/sites': 'app-service',
};
const iconByServiceId: Record<string, string> = {
  'app-service': 'app-service',
  'functions': 'function-apps',
  'application-gateway-waf': 'application-gateway',
  'azure-firewall': 'azure-firewall',
  'private-endpoint': 'private-endpoint',
  'sql-database': 'sql-server',
  'service-bus': 'service-bus',
  'key-vault': 'key-vault',
  'azure-monitor': 'azure-monitor',
};
const RESOURCE_WIDTH = 190;
const RESOURCE_HEIGHT = 86;
const CHILD_LEFT = 20;
const CHILD_TOP = 52;
const CHILD_GAP = 20;
const COLLISION_PADDING = 4;
const LEGACY_TRAFFIC_KINDS = new Set(['calls', 'connects-to', 'consumes', 'flows-to', 'invokes', 'publishes', 'reads', 'reads-from', 'receives', 'routes-to', 'sends', 'subscribes-to', 'triggers', 'writes', 'writes-to']);
const ROOT_BOUNDARY_KINDS = new Set(['tenant', 'management-group', 'landing-zone', 'subscription', 'region', 'site', 'retained-site']);

type Layout = { x: number; y: number; width: number; height: number };
type ArchitectureProposal = NonNullable<PlatformArchitectureModel['proposals']>[number];

function technical(record: PlatformRecord, key: string): string | undefined {
  const value = record[key] ?? (record.technical as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'string' ? value : undefined;
}

function savedLayout(record: PlatformRecord): Partial<Layout> {
  return (record.layout as Partial<Layout> | undefined) || {};
}

function numeric(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function overlaps(candidate: Layout, placed: Layout[]): boolean {
  return placed.some(item => !(candidate.x + candidate.width + COLLISION_PADDING <= item.x
    || item.x + item.width + COLLISION_PADDING <= candidate.x
    || candidate.y + candidate.height + COLLISION_PADDING <= item.y
    || item.y + item.height + COLLISION_PADDING <= candidate.y));
}

function nextFree(width: number, height: number, placed: Layout[], availableWidth: number, top = CHILD_TOP): { x: number; y: number } {
  const columns = Math.max(1, Math.floor((Math.max(availableWidth, width + CHILD_LEFT * 2) - CHILD_LEFT * 2 + CHILD_GAP) / (RESOURCE_WIDTH + CHILD_GAP)));
  for (let row = 0; row < 200; row += 1) for (let column = 0; column < columns; column += 1) {
    const candidate = { x: CHILD_LEFT + column * (RESOURCE_WIDTH + CHILD_GAP), y: top + row * (RESOURCE_HEIGHT + CHILD_GAP), width, height };
    if (!overlaps(candidate, placed)) return candidate;
  }
  const bottom = placed.reduce((maximum, item) => Math.max(maximum, item.y + item.height), top);
  return { x: CHILD_LEFT, y: bottom + CHILD_GAP };
}

function boundaryDefaults(record: PlatformRecord) {
  const kind = String(record.boundaryKind || 'group');
  if (kind === 'region') return { width: 560, height: 300 };
  if (kind === 'subscription' || kind === 'landing-zone') return { width: 480, height: 200 };
  if (kind === 'vnet') return { width: 420, height: 160 };
  if (kind === 'subnet') return { width: 400, height: 140 };
  if (kind === 'group') return { width: 650, height: 160 };
  return { width: 360, height: 180 };
}

function physicalParent(record: PlatformRecord, byId: Map<string, PlatformRecord>): string | undefined {
  const placement = record.placement as Record<string, unknown> | undefined;
  const supplied = (key: string) => placement && Object.prototype.hasOwnProperty.call(placement, key);
  const boundary = (candidate: unknown, boundaryKind?: string) => typeof candidate === 'string'
    && candidate !== record.id && byId.get(candidate)?.kind === 'architecture-boundary'
    && (!boundaryKind || byId.get(candidate)?.boundaryKind === boundaryKind);
  if (supplied('parentBoundaryRecordId')) return boundary(placement!.parentBoundaryRecordId) ? placement!.parentBoundaryRecordId as string : undefined;
  if (record.boundaryKind === 'subscription' && supplied('landingZoneRecordId')) return boundary(placement!.landingZoneRecordId, 'landing-zone') ? placement!.landingZoneRecordId as string : undefined;
  if (supplied('subscriptionRecordId')) return boundary(placement!.subscriptionRecordId, 'subscription') ? placement!.subscriptionRecordId as string : undefined;
  if (supplied('landingZoneRecordId')) return boundary(placement!.landingZoneRecordId, 'landing-zone') ? placement!.landingZoneRecordId as string : undefined;
  if (supplied('regionRecordId')) return boundary(placement!.regionRecordId, 'region') ? placement!.regionRecordId as string : undefined;
  return undefined;
}

function hasPlacementReference(record: PlatformRecord): boolean {
  const placement = record.placement as Record<string, unknown> | undefined;
  return ['parentBoundaryRecordId', 'subscriptionRecordId', 'regionRecordId', 'landingZoneRecordId']
    .some(key => placement && Object.prototype.hasOwnProperty.call(placement, key));
}

function drawableRecords(records: PlatformRecord[]): PlatformRecord[] {
  const byId = new Map(records.map(record => [record.id, record]));
  const resolved = new Map<string, boolean>();
  const resolving = new Set<string>();
  const resolves = (record: PlatformRecord): boolean => {
    if (resolved.has(record.id)) return resolved.get(record.id)!;
    if (resolving.has(record.id)) return false;
    resolving.add(record.id);
    const parentId = physicalParent(record, byId);
    const visible = parentId
      ? resolves(byId.get(parentId)!)
      : record.kind === 'architecture-boundary'
        && ROOT_BOUNDARY_KINDS.has(String(record.boundaryKind || ''))
        && !hasPlacementReference(record);
    resolving.delete(record.id);
    resolved.set(record.id, visible);
    return visible;
  };
  return records.filter(resolves);
}

function layoutRecords(records: PlatformRecord[]) {
  const byId = new Map(records.map(record => [record.id, record]));
  const parents = new Map<string, string>();
  records.forEach(record => {
    const parent = physicalParent(record, byId);
    if (parent) parents.set(record.id, parent);
  });
  const children = new Map<string, PlatformRecord[]>();
  for (const item of byId.values()) {
    const parent = parents.get(item.id);
    if (parent) children.set(parent, [...(children.get(parent) || []), item]);
  }
  const layouts = new Map<string, Layout>();
  const visiting = new Set<string>();
  const measure = (item: PlatformRecord): Layout => {
    if (layouts.has(item.id)) return layouts.get(item.id)!;
    if (visiting.has(item.id)) return { x: 0, y: 0, width: RESOURCE_WIDTH, height: RESOURCE_HEIGHT };
    visiting.add(item.id);
    const saved = savedLayout(item);
    const defaults = item.kind === 'architecture-boundary' ? boundaryDefaults(item) : { width: RESOURCE_WIDTH, height: RESOURCE_HEIGHT };
    const baseWidth = numeric(saved.width) ? saved.width : defaults.width;
    const baseHeight = numeric(saved.height) ? saved.height : defaults.height;
    const placed: Layout[] = [];
    for (const child of children.get(item.id) || []) {
      const size = measure(child);
      const stored = savedLayout(child);
      const preferred = numeric(stored.x) && numeric(stored.y) ? { ...size, x: stored.x, y: stored.y } : undefined;
      const childLayout = preferred && !overlaps(preferred, placed)
        ? preferred : { ...size, ...nextFree(size.width, size.height, placed, baseWidth) };
      layouts.set(child.id, childLayout);
      placed.push(childLayout);
    }
    const hasChildren = placed.length > 0;
    const layout = {
      x: numeric(saved.x) ? saved.x : 0,
      y: numeric(saved.y) ? saved.y : 0,
      width: hasChildren
        ? placed.reduce((max, child) => Math.max(max, child.x + child.width + CHILD_LEFT), defaults.width)
        : Math.min(baseWidth, defaults.width * 1.15),
      height: hasChildren
        ? placed.reduce((max, child) => Math.max(max, child.y + child.height + CHILD_LEFT), defaults.height)
        : Math.min(baseHeight, defaults.height * 1.15),
    };
    layouts.set(item.id, layout);
    visiting.delete(item.id);
    return layout;
  };
  const roots = records.filter(item => !parents.has(item.id));
  roots.forEach(measure);
  const placedRoots: Layout[] = [];
  for (const item of roots) {
    const size = layouts.get(item.id)!;
    const saved = savedLayout(item);
    const preferred = numeric(saved.x) && numeric(saved.y) ? { ...size, x: saved.x, y: saved.y } : undefined;
    const layout = preferred && !overlaps(preferred, placedRoots) ? preferred : { ...size, ...nextFree(size.width, size.height, placedRoots, 1200, 20) };
    layouts.set(item.id, layout);
    placedRoots.push(layout);
  }
  const ordered: PlatformRecord[] = [];
  const append = (item: PlatformRecord) => { ordered.push(item); (children.get(item.id) || []).forEach(append); };
  roots.forEach(append);
  return { ordered, layouts, parents };
}

function state(record: PlatformRecord): InfrastructureNodeData['state'] {
  if (record.state === 'current' || record.uses?.some(use => use.state === 'current')) return 'existing';
  return 'target';
}

function compareSteps(left: { step: PlatformProcessStep; index: number }, right: { step: PlatformProcessStep; index: number }) {
  return left.step.order - right.step.order || left.index - right.index || left.step.stepId.localeCompare(right.step.stepId);
}

function orderedSubtree(projection: PlatformArchitectureProjection | null | undefined, selectedStepId?: string): PlatformProcessStep[] {
  const all = projection?.steps || [];
  if (!all.length) return [];
  const children = new Map<string, Array<{ step: PlatformProcessStep; index: number }>>();
  all.forEach((step, index) => {
    if (!step.parentStepId) return;
    children.set(step.parentStepId, [...(children.get(step.parentStepId) || []), { step, index }]);
  });
  const selected = all.find(step => step.stepId === selectedStepId) || all[0];
  const result: PlatformProcessStep[] = [];
  const visit = (step: PlatformProcessStep) => {
    result.push(step);
    (children.get(step.stepId) || []).sort(compareSteps).forEach(child => visit(child.step));
  };
  visit(selected);
  return result;
}

function orderedInteractions(steps: PlatformProcessStep[]) {
  return steps.flatMap(step => [...(step.interactions || [])].sort((a, b) => a.order - b.order || a.linkId.localeCompare(b.linkId)));
}

function isTraffic(relationship: PlatformRelationship): boolean {
  return (typeof relationship.connectionKind === 'string' && relationship.connectionKind.trim().length > 0) || LEGACY_TRAFFIC_KINDS.has(relationship.kind);
}

function capabilityProposal(model: PlatformArchitectureModel): ArchitectureProposal | undefined {
  const relevant = (proposal: ArchitectureProposal) => proposal.operations.some(operation => operation.type === 'upsert-record' && operation.value.kind === 'platform-capability');
  const proposals = model.proposals || [];
  return [...proposals].reverse().find(item => item.status === 'pending' && relevant(item))
    || [...proposals].reverse().find(item => item.status === 'accepted' && relevant(item))
    || [...proposals].reverse().find(item => item.status === 'dismissed' && relevant(item));
}

function capabilityMemberships(model: PlatformArchitectureModel, proposal?: ArchitectureProposal): Map<string, Set<string>> {
  const capabilities = new Set(model.records.filter(item => item.kind === 'platform-capability' && !item.retiredAt).map(item => item.id));
  proposal?.operations.forEach(operation => {
    if (operation.type === 'upsert-record' && operation.value.kind === 'platform-capability' && typeof operation.value.id === 'string') capabilities.add(operation.value.id);
  });
  const memberships = new Map<string, Set<string>>();
  const add = (capability: string, member: string) => memberships.set(capability, new Set([...(memberships.get(capability) || []), member]));
  model.records.forEach(item => { if (item.capabilityRecordId && capabilities.has(item.capabilityRecordId)) add(item.capabilityRecordId, item.id); });
  const relation = (value: Record<string, unknown>) => {
    if (value.kind !== 'supports') return;
    const from = String(value.fromRecordId || ''); const to = String(value.toRecordId || '');
    if (capabilities.has(from)) add(from, to); else if (capabilities.has(to)) add(to, from);
  };
  model.relationships.forEach(relation);
  proposal?.operations.forEach(operation => {
    const value = operation.value;
    if (operation.type === 'upsert-record' && typeof value.id === 'string' && typeof value.capabilityRecordId === 'string' && capabilities.has(value.capabilityRecordId)) add(value.capabilityRecordId, value.id);
    if (operation.type === 'upsert-relationship') relation(value);
  });
  return memberships;
}

export function projectPlatformArchitecture(model: PlatformArchitectureModel, projection: PlatformArchitectureProjection | null | undefined, view: 'infrastructure' | 'process', capabilityVisible: boolean, selectedStepId?: string, draftRecordIds = new Set<string>(), agreedDesignRecordIds = new Set<string>()): InfrastructureTopology {
  const candidateRecords = model.records.filter(record => !record.retiredAt && record.kind !== 'platform-capability' && record.kind !== 'template-reference');
  const selectedSteps = view === 'process' ? orderedSubtree(projection, selectedStepId) : [];
  const interactions = orderedInteractions(selectedSteps);
  const activeIds = new Set<string>();
  selectedSteps.forEach(step => step.links.forEach(link => activeIds.add(link.recordId)));
  interactions.forEach(action => { activeIds.add(action.fromRecordId); activeIds.add(action.toRecordId); });
  const actionByRecord = new Map<string, number>();
  interactions.forEach((action, index) => { if (!actionByRecord.has(action.toRecordId)) actionByRecord.set(action.toRecordId, index + 1); });
  const actionRelationships = new Set(interactions.map(action => action.relationshipId));
  const capability = capabilityProposal(model);
  const names = new Map(model.records.map(item => [item.id, item.name]));
  const memberLabels = new Map<string, string[]>();
  if (capabilityVisible) for (const [capabilityId, members] of capabilityMemberships(model, capability)) for (const memberId of members) {
    const labels = memberLabels.get(memberId) || [];
    const label = names.get(capabilityId) || String(capability?.operations.find(operation => operation.value.id === capabilityId)?.value.name || capabilityId);
    if (!labels.includes(label)) labels.push(label);
    memberLabels.set(memberId, labels);
  }
  const { ordered, layouts, parents } = layoutRecords(drawableRecords(candidateRecords));
  const nodes: Node<InfrastructureNodeData>[] = ordered.map(record => {
    const boundary = record.kind === 'architecture-boundary';
    const layout = layouts.get(record.id)!;
    const parent = parents.get(record.id);
    return {
      id: record.id, position: { x: layout.x, y: layout.y }, ...(parent ? { parentId: parent } : {}),
      sourcePosition: Position.Right, targetPosition: Position.Left,
      style: boundary ? { width: layout.width, height: layout.height } : { width: RESOURCE_WIDTH },
      type: boundary ? 'boundary' : 'resource',
      data: {
        label: record.name, resourceName: technical(record, 'resourceName'),
        detail: boundary ? String(record.boundaryKind || 'group') : technical(record, 'resourceType') || record.purpose,
        kind: boundary ? 'boundary' : record.kind === 'deployment-resource' && technical(record, 'resourceType')?.includes('privateEndpoints') ? 'interface' : 'resource',
        icon: iconByServiceId[technical(record, 'serviceId') || ''] || iconByType[(technical(record, 'resourceType') || '').toLowerCase()], state: state(record),
        emphasis: view === 'process' ? activeIds.has(record.id) ? 'active' : boundary ? 'context' : 'muted' : 'context',
        action: actionByRecord.get(record.id), capabilityMember: memberLabels.get(record.id)?.join(','), draft: draftRecordIds.has(record.id),
        designStatus: draftRecordIds.has(record.id) ? 'needs-review' : agreedDesignRecordIds.has(record.id) ? 'agreed' : undefined,
      },
    };
  });
  const visible = new Set(nodes.map(node => node.id));
  const edges: Edge[] = model.relationships.filter(item => isTraffic(item) && visible.has(item.fromRecordId) && visible.has(item.toRecordId)).map(item => {
    const processAction = view === 'process' && actionRelationships.has(item.id);
    return {
      id: item.id, source: item.fromRecordId, target: item.toRecordId, type: 'smoothstep', label: String(item.connectionKind || item.kind),
      sourceHandle: 'right-source', targetHandle: 'left-target', markerEnd: { type: MarkerType.ArrowClosed }, data: { processAction },
      ...(view === 'process' ? { animated: processAction, style: processAction ? { stroke: '#0b63ce', strokeWidth: 3.2, opacity: 1 } : { opacity: .18 } } : {}),
    };
  });
  return { ariaLabel: view === 'process' ? 'Process overlay' : 'Target infrastructure design', nodes, edges };
}

export function projectSteps(projection: PlatformArchitectureProjection | null | undefined, model: PlatformArchitectureModel): ProcessStep[] {
  const all = projection?.steps || [];
  const roots = all.map((step, index) => ({ step, index })).filter(item => !item.step.parentStepId).sort(compareSteps);
  const byId = new Map(model.records.map(record => [record.id, record]));
  return roots.map(({ step }) => {
    const annotation = step.stepAnnotations?.[0];
    return {
      id: step.stepId, label: step.label, sourceLabel: step.trigger?.text || 'Saved process step',
      narrative: annotation?.narrative || 'This step still needs a sourced explanation.', outcome: annotation?.outcome || 'Outcome still unresolved.',
      actions: orderedInteractions(orderedSubtree(projection, step.stepId)).map(action => ({
        id: action.linkId, label: `${byId.get(action.fromRecordId)?.name || action.fromRecordId} → ${byId.get(action.toRecordId)?.name || action.toRecordId}`,
        detail: String(action.relationship?.connectionKind || action.relationship?.kind || action.kind || action.interaction),
      })),
    };
  });
}

export function selectedProposal(model: PlatformArchitectureModel) {
  const proposals = model.proposals || [];
  return [...proposals].reverse().find(item => item.status === 'pending') || [...proposals].reverse().find(item => item.status === 'accepted');
}

function proposalNames(model: PlatformArchitectureModel, proposal: ArchitectureProposal) {
  const names = new Map(model.records.map(item => [item.id, item.name]));
  proposal.operations.forEach(operation => {
    if (operation.type === 'upsert-record' && typeof operation.value.id === 'string' && typeof operation.value.name === 'string') names.set(operation.value.id, operation.value.name);
  });
  return names;
}

function placementParent(value: Record<string, unknown>): string | undefined {
  const placement = value.placement as Record<string, unknown> | undefined;
  const candidate = placement?.parentBoundaryRecordId || placement?.subscriptionRecordId || placement?.regionRecordId;
  return typeof candidate === 'string' ? candidate : undefined;
}

function proposalChanges(model: PlatformArchitectureModel, proposal: ArchitectureProposal, names: Map<string, string>): string[] {
  const existing = new Map(model.records.map(item => [item.id, item]));
  return proposal.operations.flatMap(operation => {
    const value = operation.value;
    if (operation.type === 'upsert-record') {
      const id = String(value.id || 'record'); const name = names.get(id) || String(value.name || id); const prior = existing.get(id);
      const before = prior ? placementParent(prior) : undefined;
      const candidate = prior ? { ...prior, ...value } : value;
      const after = placementParent(candidate);
      if (prior && before !== after) return `Move ${name} from ${before ? names.get(before) || before : 'placement not recorded'} to ${after ? names.get(after) || after : 'placement not recorded'}`;
      if (!prior && after) return `Add ${name} to ${names.get(after) || after}`;
      return `${prior ? 'Update' : 'Add'} ${name}`;
    }
    if (operation.type === 'upsert-relationship') {
      const from = names.get(String(value.fromRecordId)) || String(value.fromRecordId || 'source');
      const to = names.get(String(value.toRecordId)) || String(value.toRecordId || 'target');
      return `Connect ${from} to ${to} (${String(value.connectionKind || value.kind || 'relationship')})`;
    }
    if (operation.type === 'upsert-requirement') return `Record requirement: ${String(value.text || value.id || 'requirement')}`;
    if (operation.type === 'upsert-decision') return `Record decision: ${String(value.title || value.choice || value.id || 'decision')}`;
    return `Apply ${operation.type.replace(/-/g, ' ')}`;
  });
}

function proposalComparisons(model: PlatformArchitectureModel, proposal: ArchitectureProposal, names: Map<string, string>) {
  const existing = new Map(model.records.map(item => [item.id, item]));
  const relationships = new Map(model.relationships.map(item => [item.id, item]));
  const requirements = new Map(model.requirements.map(item => [item.id, item]));
  const decisions = new Map(model.decisions.map(item => [item.id, item]));
  const placement = (value: Record<string, unknown>) => { const parent = placementParent(value); return parent ? names.get(parent) || 'Named architecture boundary' : 'Placement not recorded'; };
  const humaniseField = (value: string) => value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[._-]+/g, ' ').replace(/^./, letter => letter.toUpperCase());
  const formatConfigurationValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return 'Not recorded';
    if (Array.isArray(value)) return value.map(formatConfigurationValue).join(', ');
    if (typeof value === 'object') return Object.entries(value as Record<string, unknown>)
      .map(([key, nested]) => `${humaniseField(key)}: ${formatConfigurationValue(nested)}`).join(', ');
    return String(value);
  };
  const configurationEntries = (value: Record<string, unknown>) => {
    const direct = value.configuration && typeof value.configuration === 'object'
      ? Object.entries(value.configuration as Record<string, unknown>).map(([key, entryValue]) => ({ key: `configuration.${key}`, label: humaniseField(key), value: formatConfigurationValue(entryValue) }))
      : [];
    const uses = Array.isArray(value.uses) ? value.uses.filter(use => use && typeof use === 'object') as Array<Record<string, unknown>> : [];
    const qualifyState = new Set(uses.map(use => String(use.state || '').trim()).filter(Boolean)).size > 1;
    return [...direct, ...uses.flatMap(use => {
      const configuration = use.configuration;
      if (!configuration || typeof configuration !== 'object') return [];
      const state = String(use.state || 'configuration');
      return Object.entries(configuration as Record<string, unknown>).map(([key, entryValue]) => ({
        key: `${state}.${key}`,
        label: `${qualifyState ? `${humaniseField(state)} ` : ''}${humaniseField(key)}`,
        value: formatConfigurationValue(entryValue),
      }));
    })];
  };
  const recordSummary = (value: Record<string, unknown>) => {
    const technical = value.technical as Record<string, unknown> | undefined;
    const resourceName = String(technical?.resourceName || value.resourceName || '').trim();
    const entries = configurationEntries(value);
    const configuration = entries.length ? entries.map(entry => `${entry.label}: ${entry.value}`).join(', ') : 'configuration not recorded';
    return [value.purpose, resourceName && `resource ${resourceName}`, placement(value), configuration].filter(Boolean).join('; ');
  };
  return proposal.operations.flatMap(operation => {
    const id = String(operation.value.id || 'record');
    if (operation.type === 'upsert-record') {
      const prior = existing.get(id) as unknown as Record<string, unknown> | undefined;
      const next = { ...(prior || {}), ...operation.value };
      const resource = names.get(id) || String(operation.value.name || id);
      if (!prior) return [{ resource, before: 'Not in the saved architecture', after: recordSummary(next) }];
      const comparisons = [];
      if (JSON.stringify([prior.configuration || {}, prior.uses || []]) !== JSON.stringify([next.configuration || {}, next.uses || []])) {
        const beforeConfiguration = new Map(configurationEntries(prior).map(entry => [entry.key, entry]));
        const afterConfiguration = new Map(configurationEntries(next).map(entry => [entry.key, entry]));
        new Set([...beforeConfiguration.keys(), ...afterConfiguration.keys()]).forEach(key => {
          const before = beforeConfiguration.get(key); const after = afterConfiguration.get(key);
          comparisons.push({ resource: `${resource} - ${after?.label || before?.label || humaniseField(key)}`, before: before?.value || 'Not recorded', after: after?.value || 'Not recorded' });
        });
      }
      if (prior.purpose !== next.purpose) comparisons.push({ resource: `${resource} - purpose`, before: String(prior.purpose || 'Purpose not recorded'), after: String(next.purpose || 'Purpose not recorded') });
      const priorTechnical = prior.technical as Record<string, unknown> | undefined; const nextTechnical = next.technical as Record<string, unknown> | undefined;
      const priorName = priorTechnical?.resourceName || prior.resourceName; const nextName = nextTechnical?.resourceName || next.resourceName;
      if (priorName !== nextName) comparisons.push({ resource: `${resource} - resource name`, before: String(priorName || 'Not recorded'), after: String(nextName || 'Not recorded') });
      if (placementParent(prior) !== placementParent(next)) comparisons.push({ resource: `${resource} - placement`, before: placement(prior), after: placement(next) });
      return comparisons.length ? comparisons : [{ resource, before: recordSummary(prior), after: recordSummary(next) }];
    }
    if (operation.type === 'upsert-relationship') {
      const describe = (value: Record<string, unknown>) => `${names.get(String(value.fromRecordId)) || 'Source resource'} to ${names.get(String(value.toRecordId)) || 'Target resource'} (${String(value.connectionKind || value.kind || 'connection')})`;
      const prior = relationships.get(id);
      return [{ resource: 'Architecture connection', before: prior ? describe(prior as unknown as Record<string, unknown>) : 'No saved connection', after: describe(operation.value) }];
    }
    if (operation.type === 'upsert-requirement') return [{ resource: 'Architecture requirement', before: requirements.get(id)?.text || 'No saved requirement', after: String(operation.value.text || 'Requirement text not recorded') }];
    if (operation.type === 'upsert-decision') return [{ resource: String(operation.value.title || decisions.get(id)?.title || 'Architecture decision'), before: decisions.get(id)?.choice || 'No saved decision', after: String(operation.value.choice || 'Decision not recorded') }];
    return [{ resource: operation.type.replace(/-/g, ' '), before: 'Saved design', after: 'Apply the proposed operation' }];
  });
}

export function projectProposal(model: PlatformArchitectureModel, processName: string): ChangeProposal {
  const proposal = selectedProposal(model);
  if (!proposal) return { title: 'No design proposal awaiting review', description: 'Describe a change in the shared conversation at any time.', state: 'rejected' };
  const names = proposalNames(model, proposal);
  const candidate = proposal.operations.find(item => item.type === 'upsert-record')?.value.name;
  return {
    title: String(candidate || `${processName} architecture change`), description: proposal.explanation,
    scopeLabels: proposal.scopeRecordIds.map(id => names.get(id) || id), changeLabels: proposalChanges(model, proposal, names), comparisons: proposalComparisons(model, proposal, names),
    state: proposal.status === 'pending' ? 'suggested' : proposal.status === 'accepted' ? 'agreed' : 'rejected',
  };
}

export function projectCapability(model: PlatformArchitectureModel): CapabilityReview {
  const proposal = capabilityProposal(model);
  const operation = proposal?.operations.find(item => item.type === 'upsert-record' && item.value.kind === 'platform-capability');
  const saved = model.records.find(item => item.kind === 'platform-capability' && !item.retiredAt);
  const candidate = operation?.value; const id = typeof candidate?.id === 'string' ? candidate.id : saved?.id;
  return {
    name: String(candidate?.name || saved?.name || 'Reusable capabilities'),
    purpose: String(candidate?.purpose || saved?.purpose || 'No reusable grouping has been suggested.'),
    state: proposal ? proposal.status === 'pending' ? 'suggested' : proposal.status === 'accepted' ? 'agreed' : 'rejected' : saved?.reviewState === 'reviewed' ? 'saved' : saved ? 'unreviewed' : 'rejected',
    resourceIds: id ? [...(capabilityMemberships(model, proposal).get(id) || [])] : [],
  };
}

const PILLARS = [
  { name: 'Reliability', keys: ['reliability'] }, { name: 'Security', keys: ['security'] },
  { name: 'Cost optimisation', keys: ['cost', 'cost optimisation', 'cost optimization'] },
  { name: 'Operational excellence', keys: ['operations', 'operational excellence'] },
  { name: 'Performance efficiency', keys: ['performance', 'performance efficiency'] },
] as const;

function evidencePillars(item: Record<string, unknown>): Set<string> {
  const result = new Set<string>();
  const add = (value: unknown) => { if (typeof value === 'string') result.add(value.toLowerCase()); };
  add(item.wellArchitectedPillar); add(item.frameworkPillar);
  const framework = typeof item.framework === 'string' ? item.framework.toLowerCase() : '';
  if ((framework.includes('well-architected') || framework.includes('well architected'))) add(item.pillar);
  if (Array.isArray(item.targets)) item.targets.forEach(target => {
    if (!target || typeof target !== 'object') return;
    const typed = target as Record<string, unknown>; add(typed.wellArchitectedPillar); add(typed.frameworkPillar);
    const standard = typeof (typed.framework || typed.standard) === 'string' ? String(typed.framework || typed.standard).toLowerCase() : '';
    if (standard.includes('well-architected') || standard.includes('well architected')) add(typed.pillar);
  });
  return result;
}

export function projectReview(model: PlatformArchitectureModel): ArchitectureReview {
  const evidence = [...model.requirements, ...model.decisions];
  const unresolved = evidence.filter(item => item.reviewState !== 'reviewed')
    .map(item => 'text' in item ? item.text : item.title || item.choice)
    .filter((value): value is string => typeof value === 'string' && value.length > 0);
  const grouped = PILLARS.map(pillar => evidence.filter(item => pillar.keys.some(key => evidencePillars(item as unknown as Record<string, unknown>).has(key))));
  const pillars = PILLARS.map((pillar, index) => ({
    name: pillar.name,
    state: !grouped[index].length ? 'unassessed' as const : grouped[index].some(item => item.reviewState !== 'reviewed') ? 'gap' as const : 'considered' as const,
  }));
  const hasAssessment = grouped.some(items => items.length);
  const hasGap = unresolved.length > 0 || pillars.some(pillar => pillar.state === 'gap');
  const complete = pillars.every(pillar => pillar.state === 'considered');
  return {
    stateLabel: hasGap ? 'Gaps remain' : complete ? 'Reviewed' : hasAssessment ? 'Assessment incomplete' : 'Unassessed', pillars,
    openDecisions: unresolved,
    sources: [
      { label: 'Azure Well-Architected Framework', href: 'https://learn.microsoft.com/azure/well-architected/' },
      { label: 'Cloud Adoption Framework', href: 'https://learn.microsoft.com/azure/cloud-adoption-framework/ready/landing-zone/' },
    ],
  };
}
